<template>
	<div class="quizzes-page">
		<!-- Page Header -->
		<div class="page-header">
			<h1>Quiz Administration</h1>
			<p class="subtitle">Manage quizzes, questions, and prizes</p>
		</div>

		<!-- Alerts -->
		<transition name="fade">
			<div v-if="showAlert" class="alert" :class="`alert-${alertVariant}`">
				<span class="alert-message">{{ alertMessage }}</span>
				<button class="alert-close" @click="showAlert = false">×</button>
			</div>
		</transition>

		<div class="quizzes-container">
			<!-- Quizzes List Section -->
			<AdminListSection
				title="Quizzes"
				:items="quizzes"
				:selected-id="selectedQuiz?.id"
				:loading="loading"
				search-placeholder="Search quizzes..."
				empty-message="No quizzes found"
				:search-fields="['name', 'description']"
				:show-new-button="true"
				new-button-text="New Quiz"
				@select="selectQuiz"
				@refresh="refreshQuizzes"
				@new="newQuiz"
			>
				<template #item="{ item }">
					<div class="quiz-info">
						<div class="quiz-name">{{ item.name }}</div>
						<div class="quiz-meta">
							<span class="badge" :class="{ enabled: isQuizStarted(item) && !isQuizFinished(item), disabled: !isQuizStarted(item), finished: isQuizFinished(item) }">
								{{ getQuizStatus(item) }}
							</span>
							<span class="question-count">{{ item.questions?.length || 0 }} questions</span>
						</div>
					</div>
				</template>
			</AdminListSection>

			<!-- Quiz Details Section -->
			<div v-if="selectedQuiz" class="quiz-details-section">
				<div class="section-header">
					<h2>{{ selectedQuiz.name || 'New Quiz' }}</h2>
					<div class="header-actions-group">
						<button class="action-btn secondary" @click="toggleJsonEditor">
							{{ showJsonEditor ? 'Form View' : 'JSON View' }}
						</button>
						<AdminHeaderActions
							:loading="saving"
							:has-id="!!selectedQuiz.id"
							@save="saveQuiz"
							@delete="showDeleteConfirm"
							@new="newQuiz"
						/>
					</div>
				</div>

				<div v-if="quizStarted && !quizUnlocked && !quizFinished" class="started-warning">
					This quiz has started and cannot be modified.
					<button class="action-btn secondary unlock-btn" @click="showUnlockDialog = true">Unlock</button>
				</div>

				<!-- JSON Editor -->
				<div v-if="showJsonEditor" class="json-editor-section">
					<textarea
						v-model="jsonEditorContent"
						class="json-editor-textarea"
						spellcheck="false"
					></textarea>
					<p v-if="jsonError" class="json-error">{{ jsonError }}</p>
				</div>

				<div v-else class="details-grid">
					<!-- Basic Information -->
					<div class="form-section">
						<h3>Basic Information</h3>
						<div class="form-group">
							<label>Name</label>
							<input type="text" v-model="selectedQuiz.name" class="form-input" />
						</div>
						<div class="form-group">
							<label>Description</label>
							<textarea v-model="selectedQuiz.description" class="form-textarea" rows="2"></textarea>
						</div>
						<div class="form-row">
							<div class="form-group">
								<label>Tag Line Header</label>
								<input type="text" v-model="selectedQuiz.tagLineHeader" class="form-input" />
							</div>
							<div class="form-group">
								<label>Tag Line</label>
								<input type="text" v-model="selectedQuiz.tagLine" class="form-input" />
							</div>
						</div>
						<div class="form-row">
							<div class="form-group">
								<label>Quiz Start Date/Time</label>
								<input type="datetime-local" v-model="selectedQuiz.quizStartDate" class="form-input" @change="recalculateAllQuestionDates" />
							</div>
							<div class="form-group">
								<label>Number of Questions</label>
								<div class="input-with-button">
									<input type="number" v-model.number="selectedQuiz.numberOfQuestions" class="form-input" min="1" @change="recalculateAllQuestionDates" />
									<button class="action-btn secondary" type="button" @click="generateEmptyQuestions" :disabled="!selectedQuiz.quizStartDate">Generate</button>
								</div>
							</div>
						</div>
						<div class="form-row">
							<div class="form-group">
								<label>Question Interval (seconds)</label>
								<input type="number" v-model.number="selectedQuiz.questionInterval" class="form-input" placeholder="86400 = 1 day" @change="recalculateAllQuestionDates" />
								<span class="help-text">86400 = 1 day, 43200 = 12 hours</span>
							</div>
						</div>
						<div class="form-row">
							<div class="form-group">
								<label>Advertise From</label>
								<input type="datetime-local" v-model="selectedQuiz.advertiseDateTime" class="form-input" />
								<span class="help-text">When the quiz starts appearing</span>
							</div>
							<div class="form-group">
								<label>Show Closed Until</label>
								<input type="datetime-local" v-model="selectedQuiz.showClosedUntilDateTime" class="form-input" />
								<span class="help-text">Show as closed until this date</span>
							</div>
						</div>
					</div>

					<!-- Images -->
					<div class="form-section">
						<h3>Images</h3>
						<div class="form-group">
							<label>Background URL</label>
							<div class="input-with-button">
								<input type="text" v-model="selectedQuiz.bgUrl" class="form-input" />
								<button class="action-btn" type="button" @click="setImageTarget('quiz', 'bgUrl', null); openImagePicker()">Pick</button>
							</div>
							<div v-if="selectedQuiz.bgUrl" class="image-preview-container"><img :src="selectedQuiz.bgUrl" class="image-preview" /></div>
						</div>
						<div class="form-group">
							<label>Thumbnail URL</label>
							<div class="input-with-button">
								<input type="text" v-model="selectedQuiz.thumbUrl" class="form-input" />
								<button class="action-btn" type="button" @click="setImageTarget('quiz', 'thumbUrl', null); openImagePicker()">Pick</button>
							</div>
							<div v-if="selectedQuiz.thumbUrl" class="image-preview-container"><img :src="selectedQuiz.thumbUrl" class="image-preview" /></div>
						</div>
						<div class="form-group">
							<label>Closed Thumbnail URL</label>
							<div class="input-with-button">
								<input type="text" v-model="selectedQuiz.closedThumbUrl" class="form-input" />
								<button class="action-btn" type="button" @click="setImageTarget('quiz', 'closedThumbUrl', null); openImagePicker()">Pick</button>
							</div>
							<div v-if="selectedQuiz.closedThumbUrl" class="image-preview-container"><img :src="selectedQuiz.closedThumbUrl" class="image-preview" /></div>
						</div>
						<div class="form-group">
							<label>Final Day Special Image URL</label>
							<div class="input-with-button">
								<input type="text" v-model="selectedQuiz.finalDaySpecialImageUrl" class="form-input" />
								<button class="action-btn" type="button" @click="setImageTarget('quiz', 'finalDaySpecialImageUrl', null); openImagePicker()">Pick</button>
							</div>
							<div v-if="selectedQuiz.finalDaySpecialImageUrl" class="image-preview-container"><img :src="selectedQuiz.finalDaySpecialImageUrl" class="image-preview" /></div>
						</div>
						<div class="form-group">
							<label>Terms URL</label>
							<input type="text" v-model="selectedQuiz.termsUrl" class="form-input" />
						</div>
					</div>

					<!-- Advertising -->
					<div class="form-section">
						<h3>Advertising</h3>
						<div class="form-group">
							<label>Advertising Header</label>
							<input type="text" v-model="selectedQuiz.advertisingHeader" class="form-input" />
						</div>
						<div class="form-group">
							<label>Advertising Description</label>
							<textarea v-model="selectedQuiz.advertisingDescription" class="form-textarea" rows="2"></textarea>
						</div>
						<div class="form-group">
							<label>Closed Description</label>
							<textarea v-model="selectedQuiz.closedDescription" class="form-textarea" rows="2"></textarea>
						</div>
					</div>

					<!-- Settings -->
					<div class="form-section">
						<h3>Settings</h3>
						<div class="form-row">
							<div class="form-group">
								<label>Streak Meter Max</label>
								<input type="number" v-model.number="selectedQuiz.streakMeterMax" class="form-input" />
							</div>
							<div class="form-group">
								<label>Streak Meter Interval</label>
								<input type="number" v-model.number="selectedQuiz.streakMeterInterval" class="form-input" />
							</div>
						</div>
					</div>

					<!-- Questions Section -->
					<div class="form-section full-width">
						<div class="section-header">
							<h3>Questions ({{ selectedQuiz.questions?.length || 0 }})</h3>
							<button class="action-btn secondary" @click="addQuestion">+ Add Question</button>
						</div>

						<div v-if="selectedQuiz.questions && selectedQuiz.questions.length > 0" class="questions-list">
							<div
								v-for="(question, qIndex) in selectedQuiz.questions"
								:key="question.id || qIndex"
								class="question-container"
								:class="{ dragging: dragIndex === qIndex, 'drag-over': dragOverIndex === qIndex }"
								draggable="true"
								@dragstart="onDragStart(qIndex, $event)"
								@dragover.prevent="onDragOver(qIndex)"
								@dragleave="onDragLeave"
								@drop="onDrop(qIndex)"
								@dragend="onDragEnd"
							>
								<div class="question-header" @click="toggleQuestion(qIndex)">
									<span class="drag-handle" @mousedown.stop title="Drag to reorder">&#x2630;</span>
									<span class="expand-icon" :class="{ rotated: expandedQuestions.includes(qIndex) }">></span>
									<span class="question-number">Q{{ question.questionNumber || qIndex + 1 }}</span>
									<span class="question-preview">{{ truncate(question.value, 60) }}</span>
									<button class="action-btn secondary small" @click.stop="duplicateQuestion(qIndex)">Duplicate</button>
									<button class="action-btn danger small" @click.stop="deleteQuestion(qIndex)">Delete</button>
								</div>

								<transition name="expand">
									<div v-if="expandedQuestions.includes(qIndex)" class="question-details">
										<div class="form-row">
											<div class="form-group">
												<label>Question Number</label>
												<input type="number" v-model.number="question.questionNumber" class="form-input" />
											</div>
											<div class="form-group">
												<label>Correct Points</label>
												<input type="number" v-model.number="question.correctPoints" class="form-input" />
											</div>
											<div class="form-group">
												<label>Incorrect Points</label>
												<input type="number" v-model.number="question.incorrectPoints" class="form-input" />
											</div>
										</div>

										<div class="form-group">
											<label>Question</label>
											<textarea v-model="question.value" class="form-textarea" rows="2"></textarea>
										</div>

										<div class="form-group">
											<label>Daily Description</label>
											<input type="text" v-model="question.dailyDescription" class="form-input" />
										</div>

										<div class="form-group">
											<label>Pre-amble (joke/intro)</label>
											<textarea v-model="question.preamble" class="form-textarea" rows="2"></textarea>
										</div>

										<div class="form-group">
											<label>Answer Description</label>
											<textarea v-model="question.answerDescription" class="form-textarea" rows="2"></textarea>
										</div>

										<div class="form-group">
											<label>Answer Example</label>
											<input type="text" v-model="question.answerExample" class="form-input" />
										</div>

										<div class="form-row">
											<div class="form-group">
												<label>Start Date/Time (auto-calculated)</label>
												<input type="datetime-local" v-model="question.startDateTime" class="form-input" disabled />
											</div>
											<div class="form-group">
												<label>End Date/Time (auto-calculated)</label>
												<input type="datetime-local" v-model="question.endDateTime" class="form-input" disabled />
											</div>
										</div>

										<div class="form-row">
											<div class="form-group">
												<label>Background URL</label>
												<div class="input-with-button">
													<input type="text" v-model="question.bgUrl" class="form-input" />
													<button class="action-btn" type="button" @click="setImageTarget('question', 'bgUrl', qIndex); openImagePicker()">Pick</button>
												</div>
												<div v-if="question.bgUrl" class="image-preview-container"><img :src="question.bgUrl" class="image-preview" /></div>
											</div>
											<div class="form-group">
												<label>Thumbnail URL</label>
												<div class="input-with-button">
													<input type="text" v-model="question.thumbUrl" class="form-input" />
													<button class="action-btn" type="button" @click="setImageTarget('question', 'thumbUrl', qIndex); openImagePicker()">Pick</button>
												</div>
												<div v-if="question.thumbUrl" class="image-preview-container"><img :src="question.thumbUrl" class="image-preview" /></div>
											</div>
										</div>

										<!-- Answers -->
										<div class="answers-section">
											<h4>Answers</h4>
											<div class="answers-list">
												<div
													v-for="(answer, aIndex) in question.answers"
													:key="answer.id || aIndex"
													class="answer-row"
													:class="{ correct: answer.isCorrect }"
												>
													<input
														type="text"
														v-model="answer.value"
														class="form-input answer-input"
														placeholder="Answer text..."
													/>
													<label class="checkbox-label">
														<input type="checkbox" v-model="answer.isCorrect" />
														Correct
													</label>
													<button class="action-btn danger small" @click="deleteAnswer(qIndex, aIndex)">X</button>
												</div>
											</div>
											<button class="action-btn secondary small" @click="addAnswer(qIndex)" v-if="(question.answers?.length || 0) < 4">+ Add Answer</button>
										</div>
									</div>
								</transition>
							</div>
						</div>
						<div v-else class="empty-state-small">No questions yet. Click "Add Question" to create one.</div>
					</div>

					<!-- Prizes Section -->
					<div class="form-section full-width">
						<div class="section-header">
							<h3>Prizes ({{ selectedQuiz.prizes?.length || 0 }})</h3>
							<button class="action-btn secondary" @click="addPrize">+ Add Prize</button>
						</div>

						<div v-if="selectedQuiz.prizes && selectedQuiz.prizes.length > 0" class="prizes-table">
							<table>
								<thead>
									<tr>
										<th>Order</th>
										<th>Name</th>
										<th>Description</th>
										<th>Quantity</th>
										<th>Actions</th>
									</tr>
								</thead>
								<tbody>
									<tr v-for="(prize, pIndex) in selectedQuiz.prizes" :key="prize.id || pIndex">
										<td><input type="number" v-model.number="prize.order" class="form-input small" /></td>
										<td><input type="text" v-model="prize.name" class="form-input" /></td>
										<td><input type="text" v-model="prize.description" class="form-input" /></td>
										<td><input type="number" v-model.number="prize.quantity" class="form-input small" /></td>
										<td><button class="action-btn danger small" @click="deletePrize(pIndex)">Delete</button></td>
									</tr>
								</tbody>
							</table>
						</div>
						<div v-else class="empty-state-small">No prizes yet. Click "Add Prize" to create one.</div>
					</div>

					<!-- Scheduling (Display Only) -->
					<div class="form-section">
						<h3>Scheduling</h3>
						<div class="form-row">
							<div class="form-group">
								<label>Start Date/Time</label>
								<input type="datetime-local" v-model="selectedQuiz.startDateTime" class="form-input" disabled />
							</div>
							<div class="form-group">
								<label>End Date/Time</label>
								<input type="datetime-local" v-model="selectedQuiz.endDateTime" class="form-input" disabled />
							</div>
						</div>
						<div class="form-row">
							<div class="form-group">
								<label>Advertise From</label>
								<input type="datetime-local" v-model="selectedQuiz.advertiseDateTime" class="form-input" disabled />
							</div>
							<div class="form-group">
								<label>Show Closed Until</label>
								<input type="datetime-local" v-model="selectedQuiz.showClosedUntilDateTime" class="form-input" disabled />
							</div>
						</div>
					</div>

					<!-- Reset Quiz Dates -->
					<div v-if="selectedQuiz.id" class="form-section">
						<h3>Reset Quiz Dates</h3>
						<p class="section-description">Restart the quiz with new dates for all questions.</p>
						<div class="form-row">
							<div class="form-group">
								<label>New Start Date/Time</label>
								<input type="datetime-local" v-model="resetStartDate" class="form-input" />
							</div>
							<div class="form-group">
								<label>Interval (seconds)</label>
								<input type="number" v-model.number="resetInterval" class="form-input" placeholder="86400 = 1 day" />
								<span class="help-text">86400 = 1 day</span>
							</div>
						</div>
						<button
							class="action-btn warning"
							@click="showResetDialog = true"
							:disabled="resettingDates || !resetStartDate || (quizStarted && !quizUnlocked && !quizFinished)"
						>
							{{ resettingDates ? 'Resetting...' : 'Reset All Question Dates' }}
						</button>
						<p v-if="quizStarted && !quizUnlocked && !quizFinished" class="warning-text">Quiz active - cannot reset dates.</p>
					</div>
				</div>
			</div>

			<div v-else class="empty-state-section">
				<p>Select a quiz to view details or create a new one</p>
			</div>
		</div>

		<!-- Delete Confirmation Modal -->
		<transition name="fade">
			<div v-if="deleteConfirm" class="modal-backdrop" @click="deleteConfirm = false">
				<div class="modal-dialog" @click.stop>
					<div class="modal-header">
						<h3>Confirm Delete</h3>
						<button class="modal-close" @click="deleteConfirm = false">×</button>
					</div>
					<div class="modal-body">
						<p>Are you sure you want to delete <strong>{{ selectedQuiz?.name }}</strong>?</p>
						<p>This action cannot be undone.</p>
					</div>
					<div class="modal-footer">
						<button class="action-btn secondary" @click="deleteConfirm = false">Cancel</button>
						<button class="action-btn danger" @click="deleteQuiz">Delete</button>
					</div>
				</div>
			</div>
		</transition>

		<!-- Image Search Modal -->
		<transition name="fade">
			<div v-if="showImageSearchModal" class="modal-backdrop" @click="showImageSearchModal = false">
				<div class="modal-dialog modal-large" @click.stop>
					<div class="modal-header">
						<h3>Select an Image</h3>
						<button class="modal-close" @click="showImageSearchModal = false">×</button>
					</div>
					<div class="modal-body">
						<div class="search-box">
							<input v-model="imageSearchQuery" type="text" placeholder="Search images..." class="search-input" />
						</div>
						<div v-if="cdnFiles.length > 0" class="file-grid">
							<div v-for="file in filteredCdnFiles" :key="file.fileName" class="file-card" @click="selectImage(file)">
								<div class="file-preview"><img :src="file.uri" :alt="file.fileName" /></div>
								<div class="file-card-info"><div class="file-card-name" :title="file.fileName">{{ file.fileName }}</div></div>
							</div>
						</div>
						<div v-else class="empty-state-small"><p>No images found in CDN.</p></div>
					</div>
				</div>
			</div>
		</transition>

		<!-- Unlock Confirmation Dialog -->
		<MGDialog
			v-model="showUnlockDialog"
			title="Unlock Started Quiz"
			max-width="400px"
			content-height="auto"
		>
			<template #content>
				<p class="unlock-warning">This quiz has already started. Modifying it may cause issues for users who have already participated.</p>
				<p>Type <strong>i am sure</strong> to unlock:</p>
				<input
					type="text"
					v-model="unlockConfirmText"
					class="form-input"
					placeholder="Type here..."
					@keyup.enter="confirmUnlock"
				/>
			</template>
			<template #actions>
				<button class="action-btn secondary" @click="showUnlockDialog = false; unlockConfirmText = ''">Cancel</button>
				<button
					class="action-btn danger"
					@click="confirmUnlock"
					:disabled="unlockConfirmText.toLowerCase() !== 'i am sure'"
				>
					Unlock
				</button>
			</template>
		</MGDialog>

		<!-- Reset Dates Confirmation Dialog -->
		<MGDialog
			v-model="showResetDialog"
			title="Reset Quiz Dates"
			max-width="450px"
			content-height="auto"
		>
			<template #content>
				<p class="unlock-warning">Warning: This will reset all question dates and delete any answers already submitted by users.</p>
				<p>This action cannot be undone. Are you sure you want to proceed?</p>
			</template>
			<template #actions>
				<button class="action-btn secondary" @click="showResetDialog = false">Cancel</button>
				<button class="action-btn danger" @click="confirmResetQuizDates">Reset Dates</button>
			</template>
		</MGDialog>
	</div>
</template>

<script>
import AdminServices from '@/admin/mixins/AdminServices';
import AdminHeaderActions from '@/admin/components/AdminHeaderActions.vue';
import AdminListSection from '@/admin/components/AdminListSection.vue';
import MGDialog from '@/components/elements/MGDialog.vue';

export default {
	name: 'QuizzesPage',
	components: {
		AdminHeaderActions,
		AdminListSection,
		MGDialog
	},
	mixins: [AdminServices],
	data() {
		return {
			quizzes: [],
			loading: false,
			saving: false,
			selectedQuiz: null,
			showAlert: false,
			alertMessage: '',
			alertVariant: 'success',
			deleteConfirm: false,
			expandedQuestions: [],
			showImageSearchModal: false,
			cdnFiles: [],
			imageSearchQuery: '',
			imageTarget: null,
			resetStartDate: '',
			resetInterval: 86400,
			resettingDates: false,
			dragIndex: null,
			dragOverIndex: null,
			showUnlockDialog: false,
			unlockConfirmText: '',
			quizUnlocked: false,
			showResetDialog: false,
			showJsonEditor: false,
			jsonEditorContent: '',
			jsonError: ''
		};
	},
	computed: {
		filteredCdnFiles() {
			if (!this.cdnFiles) return [];
			const query = this.imageSearchQuery.toLowerCase();
			return this.cdnFiles.filter(f =>
				f.fileName.toLowerCase().includes(query) &&
				['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].some(ext => f.fileName.toLowerCase().endsWith(ext))
			);
		},
		quizStarted() {
			if (!this.selectedQuiz?.startDateTime) return false;
			return new Date() > new Date(this.selectedQuiz.startDateTime);
		},
		quizFinished() {
			if (!this.selectedQuiz?.endDateTime) return false;
			return new Date() > new Date(this.selectedQuiz.endDateTime);
		}
	},
	mounted() {
		this.refreshQuizzes();
	},
	methods: {
		isQuizStarted(quiz) {
			if (!quiz?.startDateTime) return false;
			return new Date() > new Date(quiz.startDateTime);
		},

		isQuizFinished(quiz) {
			if (!quiz?.endDateTime) return false;
			return new Date() > new Date(quiz.endDateTime);
		},

		getQuizStatus(quiz) {
			if (!this.isQuizStarted(quiz)) return 'Not Started';
			if (this.isQuizFinished(quiz)) return 'Finished';
			return 'Active';
		},

		refreshQuizzes() {
			this.loading = true;
			this._getQuizzes(
				(data) => {
					this.quizzes = data || [];
					this.loading = false;
				},
				(error) => {
					this.showAlertMessage(error, 'error');
					this.loading = false;
				}
			);
		},

		selectQuiz(quiz) {
			this.selectedQuiz = JSON.parse(JSON.stringify(quiz));
			this.expandedQuestions = [];
			this.quizUnlocked = false;

			// Convert dates from API format to datetime-local format
			if (this.selectedQuiz.advertiseDateTime) {
				this.selectedQuiz.advertiseDateTime = this.formatDateTimeFromApi(this.selectedQuiz.advertiseDateTime);
			}
			if (this.selectedQuiz.showClosedUntilDateTime) {
				this.selectedQuiz.showClosedUntilDateTime = this.formatDateTimeFromApi(this.selectedQuiz.showClosedUntilDateTime);
			}
			if (this.selectedQuiz.questions) {
				this.selectedQuiz.questions.forEach(q => {
					if (q.startDateTime) q.startDateTime = this.formatDateTimeFromApi(q.startDateTime);
					if (q.endDateTime) q.endDateTime = this.formatDateTimeFromApi(q.endDateTime);
				});
			}

			// Infer quizStartDate from existing questions or quiz startDateTime
			if (!this.selectedQuiz.quizStartDate) {
				if (this.selectedQuiz.questions?.length > 0 && this.selectedQuiz.questions[0].startDateTime) {
					this.selectedQuiz.quizStartDate = this.selectedQuiz.questions[0].startDateTime;
				} else if (this.selectedQuiz.startDateTime) {
					this.selectedQuiz.quizStartDate = this.formatDateTimeFromApi(this.selectedQuiz.startDateTime);
				}
			}
			if (!this.selectedQuiz.questionInterval && this.selectedQuiz.questions?.length > 1) {
				const q1 = this.selectedQuiz.questions[0];
				const q2 = this.selectedQuiz.questions[1];
				if (q1.startDateTime && q2.startDateTime) {
					const diff = new Date(q2.startDateTime) - new Date(q1.startDateTime);
					this.selectedQuiz.questionInterval = Math.round(diff / 1000);
				}
			}
			if (!this.selectedQuiz.questionInterval) {
				this.selectedQuiz.questionInterval = 86400;
			}
			// Infer numberOfQuestions from existing questions
			if (this.selectedQuiz.questions?.length > 0) {
				this.selectedQuiz.numberOfQuestions = this.selectedQuiz.questions.length;
			} else if (!this.selectedQuiz.numberOfQuestions) {
				this.selectedQuiz.numberOfQuestions = 10;
			}
		},

		newQuiz() {
			this.selectedQuiz = {
				name: '',
				description: '',
				tagLineHeader: '',
				tagLine: '',
				bgUrl: '',
				thumbUrl: '',
				termsUrl: '',
				startDateTime: '',
				endDateTime: '',
				advertiseDateTime: '',
				showClosedUntilDateTime: '',
				closedThumbUrl: '',
				finalDaySpecialImageUrl: '',
				closedDescription: '',
				advertisingHeader: '',
				advertisingDescription: '',
				streakMeterMax: 25,
				streakMeterInterval: 5,
				quizStartDate: '',
				questionInterval: 86400,
				numberOfQuestions: 10,
				questions: [],
				prizes: []
			};
			this.expandedQuestions = [];
		},

		saveQuiz() {
			// Apply JSON changes first if in JSON editor mode
			if (this.showJsonEditor) {
				if (!this.applyJsonToQuiz()) {
					return;
				}
			}

			if (!this.selectedQuiz.name) {
				this.showAlertMessage('Quiz name is required', 'error');
				return;
			}

			// Prevent updates to started quizzes unless unlocked or finished
			if (this.selectedQuiz.id && this.quizStarted && !this.quizUnlocked && !this.quizFinished) {
				this.showAlertMessage('Cannot update a quiz that has already started', 'error');
				return;
			}

			// Validate advertiseDateTime - must be before quiz start date
			if (this.selectedQuiz.advertiseDateTime && this.selectedQuiz.quizStartDate) {
				if (new Date(this.selectedQuiz.advertiseDateTime) >= new Date(this.selectedQuiz.quizStartDate)) {
					this.showAlertMessage('Advertise From must be before the Quiz Start Date', 'error');
					return;
				}
			}

			// Validate showClosedUntilDateTime - must be after the last question end date
			if (this.selectedQuiz.showClosedUntilDateTime && this.selectedQuiz.quizStartDate) {
				const interval = this.selectedQuiz.questionInterval || 86400;
				const numQuestions = this.selectedQuiz.questions?.length || this.selectedQuiz.numberOfQuestions || 10;
				const startDate = new Date(this.selectedQuiz.quizStartDate);
				const lastQuestionEndDate = new Date(startDate.getTime() + (numQuestions * interval * 1000));

				if (new Date(this.selectedQuiz.showClosedUntilDateTime) <= lastQuestionEndDate) {
					this.showAlertMessage('Show Closed Until must be after the last question end date', 'error');
					return;
				}
			}

			// Create a copy for API with formatted dates
			const quizToSave = JSON.parse(JSON.stringify(this.selectedQuiz));

			// Capture local fields before removing
			const quizStartDate = quizToSave.quizStartDate;
			const questionInterval = quizToSave.questionInterval || 86400;
			const numberOfQuestions = quizToSave.numberOfQuestions || 10;

			// Remove local-only fields that the API doesn't expect
			delete quizToSave.quizStartDate;
			delete quizToSave.questionInterval;
			delete quizToSave.numberOfQuestions;

			// Set startDateTime from quizStartDate
			if (quizStartDate) {
				quizToSave.startDateTime = this.formatDateTimeForApi(quizStartDate);

				// Calculate endDateTime based on numberOfQuestions and interval
				const startDate = new Date(quizStartDate);
				const endDate = new Date(startDate.getTime() + (numberOfQuestions * questionInterval * 1000));
				quizToSave.endDateTime = this.formatDateTimeForApi(this.formatDateTimeLocal(endDate));
			}

			if (quizToSave.questions?.length > 0) {
				// Format question dates for API
				quizToSave.questions.forEach(q => {
					if (q.startDateTime) q.startDateTime = this.formatDateTimeForApi(q.startDateTime);
					if (q.endDateTime) q.endDateTime = this.formatDateTimeForApi(q.endDateTime);
				});

				// Override endDateTime from last question if questions exist
				const lastQuestion = quizToSave.questions[quizToSave.questions.length - 1];
				if (lastQuestion.endDateTime) {
					quizToSave.endDateTime = lastQuestion.endDateTime;
				}
			}

			// Format advertiseDateTime and showClosedUntilDateTime for API if set
			if (quizToSave.advertiseDateTime) {
				quizToSave.advertiseDateTime = this.formatDateTimeForApi(quizToSave.advertiseDateTime);
			}
			if (quizToSave.showClosedUntilDateTime) {
				quizToSave.showClosedUntilDateTime = this.formatDateTimeForApi(quizToSave.showClosedUntilDateTime);
			}

			// Remove empty date fields entirely (API doesn't accept null or empty strings)
			const dateFields = ['startDateTime', 'endDateTime', 'advertiseDateTime', 'showClosedUntilDateTime'];
			dateFields.forEach(field => {
				if (!quizToSave[field]) {
					delete quizToSave[field];
				}
			});

			this.saving = true;

			if (this.selectedQuiz.id) {
				const quizId = this.selectedQuiz.id;
				this._updateQuiz(
					quizId,
					quizToSave,
					() => {
						this.showAlertMessage('Quiz updated successfully', 'success');
						this.saving = false;
						// Refresh and re-select the quiz to show updated data from API
						this._getQuizzes(
							(data) => {
								this.quizzes = data || [];
								const updatedQuiz = this.quizzes.find(q => q.id === quizId);
								if (updatedQuiz) {
									this.selectQuiz(updatedQuiz);
								}
							},
							() => {}
						);
					},
					(error) => {
						this.showAlertMessage(error, 'error');
						this.saving = false;
					}
				);
			} else {
				this._createQuiz(
					quizToSave,
					(data) => {
						this.showAlertMessage('Quiz created successfully', 'success');
						this.saving = false;
						this.selectedQuiz = data;
						this.refreshQuizzes();
					},
					(error) => {
						this.showAlertMessage(error, 'error');
						this.saving = false;
					}
				);
			}
		},

		showDeleteConfirm() {
			this.deleteConfirm = true;
		},

		deleteQuiz() {
			if (!this.selectedQuiz?.id) return;

			this._deleteQuiz(
				this.selectedQuiz.id,
				() => {
					this.showAlertMessage('Quiz deleted successfully', 'success');
					this.selectedQuiz = null;
					this.deleteConfirm = false;
					this.refreshQuizzes();
				},
				(error) => {
					this.showAlertMessage(error, 'error');
					this.deleteConfirm = false;
				}
			);
		},

		confirmResetQuizDates() {
			this.showResetDialog = false;
			this.resetQuizDates();
		},

		resetQuizDates() {
			if (!this.selectedQuiz?.id || !this.resetStartDate) return;

			this.resettingDates = true;

			// Format date for API: "2026-01-12 15:00:00.000000"
			const date = new Date(this.resetStartDate);
			const formattedDate = date.getFullYear() + '-' +
				String(date.getMonth() + 1).padStart(2, '0') + '-' +
				String(date.getDate()).padStart(2, '0') + ' ' +
				String(date.getHours()).padStart(2, '0') + ':' +
				String(date.getMinutes()).padStart(2, '0') + ':' +
				String(date.getSeconds()).padStart(2, '0') + '.000000';

			const quizId = this.selectedQuiz.id;
			this._resetQuizDates(
				quizId,
				formattedDate,
				this.resetInterval,
				() => {
					this.showAlertMessage('Quiz dates reset successfully', 'success');
					this.resettingDates = false;
					// Refresh and re-select the quiz to show updated dates
					this._getQuizzes(
						(data) => {
							this.quizzes = data || [];
							const updatedQuiz = this.quizzes.find(q => q.id === quizId);
							if (updatedQuiz) {
								this.selectQuiz(updatedQuiz);
							}
						},
						() => {}
					);
				},
				(error) => {
					this.showAlertMessage(error, 'error');
					this.resettingDates = false;
				}
			);
		},

		// Question methods
		toggleQuestion(index) {
			const idx = this.expandedQuestions.indexOf(index);
			if (idx > -1) {
				this.expandedQuestions.splice(idx, 1);
			} else {
				this.expandedQuestions.push(index);
			}
		},

		addQuestion() {
			if (!this.selectedQuiz.questions) {
				this.selectedQuiz.questions = [];
			}
			const questionIndex = this.selectedQuiz.questions.length;
			const { startDateTime, endDateTime } = this.calculateQuestionDates(questionIndex);

			const newQuestion = {
				questionNumber: questionIndex + 1,
				value: '',
				dailyDescription: '',
				preAmble: '',
				answerDescription: '',
				answerExample: '',
				bgUrl: '',
				thumbUrl: '',
				startDateTime,
				endDateTime,
				correctPoints: 2,
				incorrectPoints: 1,
				answers: [
					{ value: '', isCorrect: false },
					{ value: '', isCorrect: false },
					{ value: '', isCorrect: false },
					{ value: '', isCorrect: true }
				]
			};
			this.selectedQuiz.questions.push(newQuestion);
			this.expandedQuestions.push(this.selectedQuiz.questions.length - 1);
		},

		generateEmptyQuestions() {
			if (!this.selectedQuiz.quizStartDate || !this.selectedQuiz.numberOfQuestions) return;

			const targetCount = this.selectedQuiz.numberOfQuestions;
			const currentCount = this.selectedQuiz.questions?.length || 0;

			if (targetCount <= currentCount) {
				this.showAlertMessage('Number of questions must be greater than current count to generate more', 'error');
				return;
			}

			if (!this.selectedQuiz.questions) {
				this.selectedQuiz.questions = [];
			}

			const newCount = targetCount - currentCount;
			for (let i = currentCount; i < targetCount; i++) {
				const { startDateTime, endDateTime } = this.calculateQuestionDates(i);
				this.selectedQuiz.questions.push({
					questionNumber: i + 1,
					value: '',
					dailyDescription: '',
					preAmble: '',
					answerDescription: '',
					answerExample: '',
					bgUrl: '',
					thumbUrl: '',
					startDateTime,
					endDateTime,
					correctPoints: 2,
					incorrectPoints: 1,
					answers: [
						{ value: '', isCorrect: false },
						{ value: '', isCorrect: false },
						{ value: '', isCorrect: false },
						{ value: '', isCorrect: true }
					]
				});
			}

			this.showAlertMessage(`Generated ${newCount} new question${newCount > 1 ? 's' : ''}`, 'success');
		},

		deleteQuestion(index) {
			this.selectedQuiz.questions.splice(index, 1);
			const expIdx = this.expandedQuestions.indexOf(index);
			if (expIdx > -1) {
				this.expandedQuestions.splice(expIdx, 1);
			}
		},

		duplicateQuestion(index) {
			const original = this.selectedQuiz.questions[index];
			const copy = JSON.parse(JSON.stringify(original));
			// Clear ID so it gets a new one on save
			delete copy.id;
			copy.answers.forEach(a => delete a.id);

			// Calculate correct dates for the new position at the end
			const newIndex = this.selectedQuiz.questions.length;
			const { startDateTime, endDateTime } = this.calculateQuestionDates(newIndex);
			copy.questionNumber = newIndex + 1;
			copy.startDateTime = startDateTime;
			copy.endDateTime = endDateTime;

			this.selectedQuiz.questions.push(copy);
			// Expand the new question
			this.expandedQuestions.push(this.selectedQuiz.questions.length - 1);
		},

		// Drag and drop methods
		onDragStart(index, event) {
			this.dragIndex = index;
			event.dataTransfer.effectAllowed = 'move';
		},

		onDragOver(index) {
			if (this.dragIndex !== index) {
				this.dragOverIndex = index;
			}
		},

		onDragLeave() {
			this.dragOverIndex = null;
		},

		onDrop(index) {
			if (this.dragIndex === null || this.dragIndex === index) return;

			const questions = this.selectedQuiz.questions;
			const draggedItem = questions.splice(this.dragIndex, 1)[0];

			// When dragging from lower to higher index, adjust for the removed item
			const targetIndex = this.dragIndex < index ? index - 1 : index;
			questions.splice(targetIndex, 0, draggedItem);

			// Recalculate dates based on new order
			this.recalculateQuestionDates();

			this.dragIndex = null;
			this.dragOverIndex = null;
		},

		onDragEnd() {
			this.dragIndex = null;
			this.dragOverIndex = null;
		},

		recalculateQuestionDates() {
			// Use the new method for consistency
			this.recalculateAllQuestionDates();
		},

		formatDateTimeLocal(date) {
			const year = date.getFullYear();
			const month = String(date.getMonth() + 1).padStart(2, '0');
			const day = String(date.getDate()).padStart(2, '0');
			const hours = String(date.getHours()).padStart(2, '0');
			const minutes = String(date.getMinutes()).padStart(2, '0');
			return `${year}-${month}-${day}T${hours}:${minutes}`;
		},

		formatDateTimeForApi(dateTimeLocal) {
			if (!dateTimeLocal) return null;
			const date = new Date(dateTimeLocal);
			return date.getFullYear() + '-' +
				String(date.getMonth() + 1).padStart(2, '0') + '-' +
				String(date.getDate()).padStart(2, '0') + 'T' +
				String(date.getHours()).padStart(2, '0') + ':' +
				String(date.getMinutes()).padStart(2, '0') + ':' +
				String(date.getSeconds()).padStart(2, '0');
		},

		formatDateTimeFromApi(apiDateTime) {
			if (!apiDateTime) return '';
			const date = new Date(apiDateTime);
			if (isNaN(date.getTime())) return '';
			return this.formatDateTimeLocal(date);
		},

		calculateQuestionDates(questionIndex) {
			const quizStart = this.selectedQuiz.quizStartDate;
			const interval = this.selectedQuiz.questionInterval || 86400;

			if (!quizStart) {
				return { startDateTime: '', endDateTime: '' };
			}

			const baseDate = new Date(quizStart);
			const intervalMs = interval * 1000;

			const startTime = new Date(baseDate.getTime() + (questionIndex * intervalMs));
			const endTime = new Date(startTime.getTime() + intervalMs);

			return {
				startDateTime: this.formatDateTimeLocal(startTime),
				endDateTime: this.formatDateTimeLocal(endTime)
			};
		},

		recalculateAllQuestionDates() {
			if (!this.selectedQuiz?.questions) return;

			this.selectedQuiz.questions.forEach((question, index) => {
				const { startDateTime, endDateTime } = this.calculateQuestionDates(index);
				question.startDateTime = startDateTime;
				question.endDateTime = endDateTime;
				question.questionNumber = index + 1;
			});
		},

		// Answer methods
		addAnswer(questionIndex) {
			if (!this.selectedQuiz.questions[questionIndex].answers) {
				this.selectedQuiz.questions[questionIndex].answers = [];
			}
			this.selectedQuiz.questions[questionIndex].answers.push({
				value: '',
				isCorrect: false
			});
		},

		deleteAnswer(questionIndex, answerIndex) {
			this.selectedQuiz.questions[questionIndex].answers.splice(answerIndex, 1);
		},

		// Prize methods
		addPrize() {
			if (!this.selectedQuiz.prizes) {
				this.selectedQuiz.prizes = [];
			}
			this.selectedQuiz.prizes.push({
				order: this.selectedQuiz.prizes.length,
				name: '',
				description: '',
				imageUrl: '',
				quantity: 1
			});
		},

		deletePrize(index) {
			this.selectedQuiz.prizes.splice(index, 1);
		},

		// Utility methods
		truncate(str, length) {
			if (!str) return '';
			return str.length > length ? str.substring(0, length) + '...' : str;
		},

		showAlertMessage(message, variant = 'success') {
			this.alertMessage = message;
			this.alertVariant = variant;
			this.showAlert = true;
			setTimeout(() => {
				this.showAlert = false;
			}, 5000);
		},

		confirmUnlock() {
			if (this.unlockConfirmText.toLowerCase() === 'i am sure') {
				this.quizUnlocked = true;
				this.showUnlockDialog = false;
				this.unlockConfirmText = '';
				this.showAlertMessage('Quiz unlocked for editing', 'success');
			}
		},

		// Image picker methods
		setImageTarget(type, field, questionIndex) {
			this.imageTarget = { type: type, field: field, questionIndex: questionIndex };
		},

		openImagePicker() {
			this.showImageSearchModal = true;
			this.imageSearchQuery = '';
			this.loadCdnFiles();
		},

		loadCdnFiles() {
			this._getFiles(files => {
				this.cdnFiles = files || [];
			});
		},

		selectImage(file) {
			if (!this.imageTarget) return;

			if (this.imageTarget.type === 'quiz') {
				this.selectedQuiz[this.imageTarget.field] = file.uri;
			} else if (this.imageTarget.type === 'question' && this.imageTarget.questionIndex !== null) {
				this.selectedQuiz.questions[this.imageTarget.questionIndex][this.imageTarget.field] = file.uri;
			}

			this.showImageSearchModal = false;
			this.imageTarget = null;
		},

		toggleJsonEditor() {
			this.jsonError = '';
			if (this.showJsonEditor) {
				// Switching to form view - apply JSON changes
				try {
					const parsed = JSON.parse(this.jsonEditorContent);
					this.selectedQuiz = parsed;
					this.showJsonEditor = false;
				} catch (e) {
					this.jsonError = 'Invalid JSON: ' + e.message;
				}
			} else {
				// Switching to JSON view
				this.jsonEditorContent = JSON.stringify(this.selectedQuiz, null, 2);
				this.showJsonEditor = true;
			}
		},

		applyJsonToQuiz() {
			try {
				const parsed = JSON.parse(this.jsonEditorContent);
				this.selectedQuiz = parsed;
				this.jsonError = '';
				return true;
			} catch (e) {
				this.jsonError = 'Invalid JSON: ' + e.message;
				return false;
			}
		}
	}
};
</script>

<style scoped>
@import '@/admin/styles/admin-common.css';

.quizzes-page {
	padding: 20px;
}

.quizzes-container {
	display: flex;
	gap: 20px;
	align-items: flex-start;
}

.quizzes-list-section {
	width: 300px;
	min-width: 300px;
	background: var(--admin-bg-secondary);
	border-radius: 8px;
	padding: 20px;
}

.quiz-details-section {
	flex: 1;
	background: var(--admin-bg-secondary);
	border-radius: 8px;
	padding: 20px;
	max-height: calc(100vh - 180px);
	overflow-y: auto;
}

.quizzes-list {
	display: flex;
	flex-direction: column;
	gap: 8px;
	max-height: 400px;
	overflow-y: auto;
}

.quiz-item {
	padding: 12px;
	border-radius: 6px;
	cursor: pointer;
	background: var(--admin-hover-bg);
	transition: all 0.2s;
}

.quiz-item:hover {
	background: var(--admin-active-bg);
}

.quiz-item.active {
	background: var(--admin-active-bg);
	border-left: 3px solid var(--background-color-alt6);
}

.quiz-name {
	font-weight: bold;
	margin-bottom: 4px;
}

.quiz-meta {
	display: flex;
	gap: 8px;
	align-items: center;
	font-size: 12px;
}

.question-count {
	color: var(--admin-text-secondary);
}

.details-grid {
	display: flex;
	flex-direction: column;
	gap: 20px;
}

.form-section {
	background: var(--admin-hover-bg);
	padding: 16px;
	border-radius: 8px;
}

.form-section.full-width {
	width: 100%;
}

.form-section h3 {
	margin: 0 0 16px 0;
	font-size: 16px;
	border-bottom: 1px solid var(--admin-border);
	padding-bottom: 8px;
}

.form-row {
	display: flex;
	gap: 16px;
}

.form-row .form-group {
	flex: 1;
}

.form-group {
	margin-bottom: 12px;
}

.form-group label {
	display: block;
	margin-bottom: 4px;
	font-size: 12px;
	color: var(--admin-text-secondary);
}

.help-text {
	display: block;
	font-size: 11px;
	color: var(--admin-text-secondary);
	margin-top: 4px;
}

.section-description {
	font-size: 13px;
	color: var(--admin-text-secondary);
	margin: 0 0 12px 0;
}

.warning-text {
	font-size: 12px;
	color: #ff9800;
	margin: 8px 0 0 0;
}

.started-warning {
	background: rgba(244, 67, 54, 0.15);
	border: 1px solid rgba(244, 67, 54, 0.4);
	color: #f44336;
	padding: 12px 16px;
	border-radius: 6px;
	margin-bottom: 16px;
	font-size: 14px;
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
}

.unlock-btn {
	flex-shrink: 0;
}

.unlock-warning {
	color: #f44336;
	margin-bottom: 12px;
}

.form-input {
	width: 100%;
	padding: 8px 12px;
	border: 1px solid var(--admin-border);
	border-radius: 4px;
	background: var(--admin-bg-secondary);
	color: var(--admin-text-primary);
	font-size: 14px;
}

.form-input.small {
	width: 80px;
}

.form-input:disabled {
	background: var(--admin-hover-bg);
	color: var(--admin-text-secondary);
	cursor: not-allowed;
	opacity: 0.7;
}

.form-textarea {
	width: 100%;
	padding: 8px 12px;
	border: 1px solid var(--admin-border);
	border-radius: 4px;
	background: var(--admin-bg-secondary);
	color: var(--admin-text-primary);
	font-size: 14px;
	resize: vertical;
}

/* Questions */
.questions-list {
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.question-container {
	border: 1px solid var(--admin-border);
	border-radius: 6px;
	overflow: hidden;
}

.question-header {
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 12px;
	cursor: pointer;
	background: var(--admin-bg-secondary);
}

.question-header:hover {
	background: var(--admin-hover-bg);
}

.drag-handle {
	cursor: grab;
	padding: 0 8px;
	color: var(--admin-text-secondary);
	font-size: 16px;
}

.drag-handle:active {
	cursor: grabbing;
}

.question-container.dragging {
	opacity: 0.5;
}

.question-container.drag-over {
	border-top: 2px solid var(--background-color-alt6);
}

.expand-icon {
	font-weight: bold;
	transition: transform 0.2s;
}

.expand-icon.rotated {
	transform: rotate(90deg);
}

.question-number {
	font-weight: bold;
	min-width: 40px;
}

.question-preview {
	flex: 1;
	color: var(--admin-text-secondary);
	font-size: 13px;
}

.question-details {
	padding: 16px;
	background: var(--admin-bg-secondary);
	border-top: 1px solid var(--admin-border);
}

/* Answers */
.answers-section {
	margin-top: 16px;
	padding-top: 16px;
	border-top: 1px solid var(--admin-border);
}

.answers-section h4 {
	margin: 0 0 12px 0;
	font-size: 14px;
}

.answers-list {
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.answer-row {
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 8px;
	border-radius: 4px;
	background: var(--admin-hover-bg);
}

.answer-row.correct {
	background: rgba(76, 175, 80, 0.2);
	border: 1px solid rgba(76, 175, 80, 0.5);
}

.answer-input {
	flex: 1;
}

.checkbox-label {
	display: flex;
	align-items: center;
	gap: 4px;
	font-size: 12px;
	white-space: nowrap;
}

/* Prizes Table */
.prizes-table {
	overflow-x: auto;
}

.prizes-table table {
	width: 100%;
	border-collapse: collapse;
}

.prizes-table th,
.prizes-table td {
	padding: 8px;
	text-align: left;
	border-bottom: 1px solid var(--admin-border);
}

.prizes-table th {
	font-size: 12px;
	color: var(--admin-text-secondary);
}

/* Badges */
.badge {
	padding: 2px 8px;
	border-radius: 4px;
	font-size: 11px;
}

.badge.enabled {
	background: rgba(76, 175, 80, 0.2);
	color: #4caf50;
}

.badge.disabled {
	background: rgba(244, 67, 54, 0.2);
	color: #f44336;
}

.badge.finished {
	background: rgba(158, 158, 158, 0.2);
	color: #9e9e9e;
}

/* Buttons */
.action-btn {
	padding: 8px 16px;
	border: none;
	border-radius: 4px;
	cursor: pointer;
	font-size: 14px;
	transition: all 0.2s;
}

.action-btn.primary {
	background: var(--background-color-alt6);
	color: white;
}

.action-btn.secondary {
	background: var(--admin-hover-bg);
	color: var(--admin-text-primary);
	border: 1px solid var(--admin-border);
}

.action-btn.danger {
	background: #f44336;
	color: white;
}

.action-btn.warning {
	background: #ff9800;
	color: white;
}

.action-btn.small {
	padding: 4px 8px;
	font-size: 12px;
}

.action-btn:disabled {
	opacity: 0.5;
	cursor: not-allowed;
}

.full-width {
	width: 100%;
}

.mt-10 {
	margin-top: 10px;
}

/* Empty States */
.empty-state,
.empty-state-small,
.empty-state-section {
	padding: 20px;
	text-align: center;
	color: var(--admin-text-secondary);
}

.empty-state-section {
	flex: 1;
	display: flex;
	align-items: center;
	justify-content: center;
	background: var(--admin-bg-secondary);
	border-radius: 8px;
	min-height: 300px;
}

/* Modal */
.modal-backdrop {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: rgba(0, 0, 0, 0.5);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 1000;
}

.modal-dialog {
	background: var(--admin-bg-secondary);
	border-radius: 8px;
	width: 400px;
	max-width: 90%;
}

.modal-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 16px;
	border-bottom: 1px solid var(--admin-border);
}

.modal-header h3 {
	margin: 0;
}

.modal-close {
	background: none;
	border: none;
	font-size: 20px;
	cursor: pointer;
	color: var(--admin-text-secondary);
}

.modal-body {
	padding: 16px;
}

.modal-footer {
	display: flex;
	justify-content: flex-end;
	gap: 8px;
	padding: 16px;
	border-top: 1px solid var(--admin-border);
}

/* Alerts */
.alert {
	padding: 12px 16px;
	border-radius: 6px;
	margin-bottom: 20px;
	display: flex;
	justify-content: space-between;
	align-items: center;
}

.alert-success {
	background: rgba(76, 175, 80, 0.2);
	color: #4caf50;
}

.alert-error {
	background: rgba(244, 67, 54, 0.2);
	color: #f44336;
}

.alert-close {
	background: none;
	border: none;
	cursor: pointer;
	font-size: 16px;
	color: inherit;
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
	transition: opacity 0.3s;
}

.fade-enter-from,
.fade-leave-to {
	opacity: 0;
}

.expand-enter-active,
.expand-leave-active {
	transition: all 0.3s;
	overflow: hidden;
}

.expand-enter-from,
.expand-leave-to {
	opacity: 0;
	max-height: 0;
}

/* Loading */
.loading {
	text-align: center;
	padding: 40px;
}

.spinner {
	width: 40px;
	height: 40px;
	border: 3px solid var(--admin-border);
	border-top-color: var(--background-color-alt6);
	border-radius: 50%;
	animation: spin 1s linear infinite;
	margin: 0 auto 16px;
}

@keyframes spin {
	to {
		transform: rotate(360deg);
	}
}

/* Section Header */
.section-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 16px;
}

.section-header h2,
.section-header h3 {
	margin: 0;
}

.header-actions-group {
	display: flex;
	gap: 8px;
	align-items: center;
}

/* JSON Editor */
.json-editor-section {
	background: var(--admin-hover-bg);
	border-radius: 8px;
	padding: 16px;
}

.json-editor-textarea {
	width: 100%;
	min-height: 500px;
	padding: 12px;
	border: 1px solid var(--admin-border);
	border-radius: 4px;
	background: var(--admin-bg-secondary);
	color: var(--admin-text-primary);
	font-size: 13px;
	font-family: monospace;
	resize: vertical;
	white-space: pre;
	overflow-x: auto;
}

.json-error {
	color: #f44336;
	font-size: 13px;
	margin: 8px 0 0 0;
}

/* Search */
.search-box {
	margin-bottom: 16px;
}

.search-input {
	width: 100%;
	padding: 8px 12px;
	border: 1px solid var(--admin-border);
	border-radius: 4px;
	background: var(--admin-bg-secondary);
	color: var(--admin-text-primary);
}

/* Page Header */
.page-header {
	margin-bottom: 20px;
}

.page-header h1 {
	margin: 0 0 4px 0;
}

.subtitle {
	color: var(--admin-text-secondary);
	margin: 0;
}

/* Responsive */
/* Image Picker */
.input-with-button {
	display: flex;
	gap: 8px;
}

.input-with-button .form-input {
	flex: 1;
}

.image-preview-container {
	margin-top: 8px;
}

.image-preview {
	max-width: 200px;
	max-height: 120px;
	border-radius: 4px;
	border: 1px solid var(--admin-border);
}

.modal-large {
	width: 800px;
	max-width: 90%;
	max-height: 80vh;
}

.modal-large .modal-body {
	max-height: 60vh;
	overflow-y: auto;
}

.file-grid {
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
	gap: 12px;
	margin-top: 16px;
}

.file-card {
	background: var(--admin-hover-bg);
	border-radius: 8px;
	overflow: hidden;
	cursor: pointer;
	transition: all 0.2s;
	border: 2px solid transparent;
}

.file-card:hover {
	border-color: var(--background-color-alt6);
	transform: translateY(-2px);
}

.file-preview {
	width: 100%;
	height: 100px;
	display: flex;
	align-items: center;
	justify-content: center;
	background: var(--admin-bg-secondary);
}

.file-preview img {
	max-width: 100%;
	max-height: 100%;
	object-fit: contain;
}

.file-card-info {
	padding: 8px;
}

.file-card-name {
	font-size: 11px;
	color: var(--admin-text-secondary);
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

@media (max-width: 768px) {
	.quizzes-container {
		flex-direction: column;
	}

	.quizzes-list-section {
		width: 100%;
		min-width: unset;
	}

	.form-row {
		flex-direction: column;
		gap: 0;
	}
}
</style>
